import { useEffect, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { RUSSIAN_LANGUAGE, SITE_API_ENABLED, SITE_API_NOTE } from "../../api/config"
import { describeSiteError } from "../../api/http"
import { keys } from "../../api/keys"
import { catalogWrites, siteWrites } from "../../api/mutations"
import { useGroupCatalogs, useGroups } from "../../api/queries"
import type { WebsiteIdentity, Catalog } from "../../api/types"
import { useAction } from "../../hooks/useAction"
import { useConfirm } from "../../hooks/useConfirm"
import { useLanguageId } from "../../lib/language"
import { ActionNotes, Note } from "../Note"
import { useOnSite } from "./useOnSite"

/** Поля группы, нужные проверкам бэка. Приходят и из списка групп
 *  (`ProductGroupIdentity`), и из карточки (`ProductGroup`). */
export interface SiteGroup {
	id: string
	isImageChecked: boolean
	isDescriptionChecked: boolean
}

/**
 * Всё, что делается с одной группой на выбранном сайте: загрузить и убрать.
 * Третьего состояния у Риги нет — ни галки «выводить в каталоге», ни отдельной
 * видимости на сайте (`groups/status/show|unshow`). Что загружено, то и видно.
 *
 * Обе операции идут в **рижский узел** (`siteWrites` → `/ai/site/*`): в базы
 * сайтов Риги ходит только он. Загрузка собирает строки одной группы теми же
 * функциями ERP, что и `ecm`, чистит остатки прежней выгрузки и дописывает
 * новые строки; всё, что она сделала, ложится в журнал — операция обратима.
 *
 * Проверки бэка повторяем до нажатия, чтобы вместо «409 Изображения не
 * проверены» человек сразу видел, что чинить. Ту единственную проверку, которую
 * можно закрыть самим, интерфейс закрывает молча — см. `setShowStatus`.
 *
 * Блок общий для карточки группы и экрана загрузки: обе страницы делают с
 * группой одно и то же.
 *
 * Порядок действий (`<Steps>` ниже) тоже общий по той же причине: до загрузки
 * нужно пройти по всей карточке, а не только по этой вкладке, и человеку,
 * открывшему панель с `/publish`, это не менее нужно, чем открывшему её из
 * карточки группы.
 */
export default function GroupSitePanel({
	group,
	site,
	siteCatalog,
	productCount,
	disabled,
}: {
	group: SiteGroup
	site: WebsiteIdentity
	siteCatalog: Catalog
	/** Состав группы, если он известен вызывающему экрану. */
	productCount?: number
	disabled: boolean
}) {
	const client = useQueryClient()
	const languageId = useLanguageId()
	const { ask, dialog } = useConfirm()

	const action = useAction({
		describe: describeSiteError,
		after: () =>
			Promise.all([
				client.invalidateQueries({ queryKey: keys.siteStatus(group.id) }),
				client.invalidateQueries({ queryKey: keys.groupCatalogs(group.id) }),
				// загрузка проставляет вывод — список «что загрузка пропустит» устарел
				client.invalidateQueries({ queryKey: keys.notReadyToDeploy() }),
			]),
	})

	// Галку «описание проверено» бэк смотрит на русском, а список отдаёт её на
	// языке запроса (`ProductGroupRepository.cs:58`). Русский набор тяжёлый
	// (≈665 КБ), поэтому и тянется только там, где открыт этот блок.
	const needRussian = languageId !== RUSSIAN_LANGUAGE
	const russian = useGroups(RUSSIAN_LANGUAGE)

	const catalogs = useGroupCatalogs(group.id)
	const onSite = useOnSite(group.id, site.id, productCount)

	/** Строки привязки этой группы к каталогу сайта. */
	const rows = useMemo(
		() => (catalogs.data ?? []).filter(row => row.catalogId === siteCatalog.id),
		[catalogs.data, siteCatalog.id]
	)
	const inCatalog = rows.length > 0

	const descriptionChecked = needRussian
		? ((russian.data ?? []).find(item => item.id === group.id)
				?.isDescriptionChecked ?? null)
		: group.isDescriptionChecked

	// сменили группу или сайт — старые сообщения к новой паре не относятся
	const { reset } = action
	useEffect(reset, [group.id, site.id, reset])

	// Привязки к каталогу сайта среди причин отказа нет намеренно: если её не
	// хватает, рижский узел проставит её сам первым делом при загрузке
	// (`AttachToCatalogAsync`) — копией веб-категорий группы из других
	// каталогов. Человеку тут решать нечего, он уже нажал «Загрузить».
	const blockers: string[] = []
	if (!group.isImageChecked)
		blockers.push("не стоит галка «изображения проверены»")
	if (descriptionChecked === false)
		blockers.push("не стоит галка «описание проверено» на русском")
	if (productCount === 0) blockers.push("в группе нет товаров")

	/**
	 * Галки «выводить в каталоге» в рижском интерфейсе нет: загрузили — значит
	 * на сайте, убрали — значит нет. Но в базе флаг пока живёт и решает две
	 * вещи: без него `add-group` отвечает `422`
	 * (`SiteProductService.cs:268-270`), а с ним следующая **массовая** загрузка
	 * вернёт на сайт то, что только что убрали (`DeployChecker.cs:81`). Поэтому
	 * интерфейс держит флаг сам и молча: ставит при загрузке, снимает при
	 * удалении. Когда систему уберут в самом ecm (задача на российскую сторону —
	 * см. PLAN.md), эти два вызова отсюда уйдут.
	 */
	function setShowStatus(showStatus: boolean) {
		if (rows.length === 0 || rows.every(row => row.showStatus === showStatus)) {
			return Promise.resolve()
		}
		return catalogWrites.setShowStatus(
			rows.map(row => ({
				productGroupId: row.productGroupId,
				catalogId: row.catalogId,
				webCategoryId: row.webCategoryId,
				catalogCategoryId: row.catalogCategoryId,
				showStatus,
			}))
		)
	}

	// Отчёт узла показываем целиком: сколько строк и в скольких таблицах.
	// `action.run` без `done` своё сообщение не ставит — остаётся это.
	function upload() {
		void action.run(async () => {
			await setShowStatus(true)
			const report = await siteWrites.add(group.id, site.id)
			action.setNotice(
				(report.attachedToCatalog > 0
					? `Группа ${group.id} прописана в каталог «${siteCatalog.name}» и загружена на ${site.name}: `
					: `Группа ${group.id} загружена на ${site.name}: `) +
					`${rowsOf(report.inserted)}.` +
					(report.verifiedOnSite ? "" : " Проверка на сайте её не нашла — посмотрите журнал узла.")
			)
		})
	}

	/** Сначала снимаем с сайта, потом гасим флаг: если удаление не прошло,
	 *  каталог остаётся в прежнем состоянии. */
	async function remove() {
		const yes = await ask({
			title: `Убрать группу ${group.id} с ${site.name}?`,
			text: "Товары группы исчезнут с сайта сразу. Вернуть их можно только новой загрузкой.",
			confirm: "Убрать",
			danger: true,
		})
		if (!yes) return
		void action.run(async () => {
			const report = await siteWrites.remove(group.id, site.id)
			await setShowStatus(false)
			action.setNotice(
				`Группа ${group.id} убрана с ${site.name}: ${rowsOf(report.deleted)}.`
			)
		})
	}

	// Загрузка, снятие и проверка идут в рижский BFF — со стенда его не видно,
	// пока не задан `RIGA_AI_ORIGIN` (`SITE_API_ENABLED`).
	const siteOff = disabled || !SITE_API_ENABLED

	return (
		<>
			{!SITE_API_ENABLED && <Note tone="hint">{SITE_API_NOTE}</Note>}

			<Steps />

			<div className="pub-checks">
				<Check ok={group.isImageChecked} label="изображения проверены" />
				<Check
					ok={descriptionChecked}
					label="описание проверено (рус.)"
					loading={needRussian && russian.isLoading}
				/>
				<Check
					ok={inCatalog}
					label={`в каталоге ${siteCatalog.name}`}
					neutral
					title={
						inCatalog
							? undefined
							: "Группа не назначена в каталог сайта. Загрузка пропишет её сама — по веб-категориям группы"
					}
				/>
				<Check
					ok={onSite.isOnSite ?? null}
					label={
						onSite.empty
							? "на сайте — в группе нет товаров"
							: onSite.isOnSite === undefined
								? "на сайте — не проверяли"
								: onSite.isOnSite
									? "уже на сайте"
									: "на сайте нет"
					}
					loading={onSite.checking}
					neutral
				/>
			</div>

			{onSite.error && (
				<Note tone="error">{describeSiteError(onSite.error)}</Note>
			)}

			{blockers.length > 0 && onSite.isOnSite !== true && (
				<Note tone="warn">Загрузка не пройдёт: {blockers.join("; ")}.</Note>
			)}

			<div className="pub-actions">
				<button
					className="btn btn--primary"
					disabled={
						siteOff ||
						action.busy ||
						blockers.length > 0 ||
						onSite.isOnSite === true
					}
					title={
						!SITE_API_ENABLED
							? SITE_API_NOTE
							: onSite.isOnSite === true
								? "Группа уже на сайте — бэк ответит «Already on site»"
								: undefined
					}
					onClick={upload}
				>
					Загрузить на сайт
				</button>

				<button
					className="btn"
					disabled={!SITE_API_ENABLED || onSite.checking || onSite.empty}
					title={
						!SITE_API_ENABLED
							? SITE_API_NOTE
							: onSite.empty
								? "В группе нет товаров — проверять нечего"
								: "Спросить рижский узел заново"
					}
					onClick={onSite.check}
				>
					Проверить на сайте
				</button>

				<span className="pub-actions__spacer" />

				<button
					className="btn btn--danger"
					disabled={siteOff || action.busy}
					title={
						!SITE_API_ENABLED
							? SITE_API_NOTE
							: "Уберёт строки группы с сайта. Операция ложится в журнал узла — её можно отыграть назад"
					}
					onClick={() => void remove()}
				>
					Убрать с сайта
				</button>

				{action.busy && <span className="spinner" />}
			</div>

			{dialog}
			<ActionNotes action={action} />
		</>
	)
}

/** «69 строк в 11 таблицах» — из отчёта рижского узла. */
function rowsOf(tables: Record<string, number>): string {
	const touched = Object.values(tables).filter(count => count > 0)
	const rows = touched.reduce((sum, count) => sum + count, 0)
	return `${rows} ${plural(rows, "строка", "строки", "строк")} в ${touched.length} ${plural(touched.length, "таблице", "таблицах", "таблицах")}`
}

function plural(count: number, one: string, few: string, many: string): string {
	const tail = count % 100
	if (tail > 10 && tail < 20) return many
	const last = count % 10
	if (last === 1) return one
	if (last >= 2 && last <= 4) return few
	return many
}

/**
 * Порядок действий перед загрузкой. Короткая шпаргалка, а не проверка —
 * бэк проверяет только фото и описание (см. `blockers` выше), остальное
 * приходится помнить самому.
 *
 * Пункт 3 честно называет нынешнее ограничение: `generate-short-names`
 * пересобирает имена по всей базе, группового варианта у бэка нет
 * (`SiteNameHelp.tsx`). Сделать его прицельным по группе — заявка на стороне
 * ECM, см. `PLAN.md`, раздел «Отложено».
 */
function Steps() {
	return (
		<Note tone="info">
			<b>Порядок действий</b>
			<ol>
				<li>
					Заполнить карточку группы целиком: категории, описание и фото,
					переводы, характеристики товаров и сами товары в группе — на всех
					нужных языках.
				</li>
				<li>
					Проставить «фото проверены» и «описание проверено» на каждом языке
					(вкладка «Карточка», язык — в шапке).
				</li>
				<li>
					Сгенерировать короткие наименования — кнопка «Имя на сайте» на
					«Табличной части». Сейчас пересобирает по всей базе, а не только по
					этой группе, — жать один раз, когда всё остальное уже готово.
				</li>
				<li>Нажать «Загрузить на сайт».</li>
			</ol>
			<p className="muted">
				После того как на сайт уедут все нужные группы: на самом сайте
				обновить модуль СЕОген и мегафильтр — это отдельная операция на
				стороне сайта, не в ERP.
			</p>
		</Note>
	)
}

/** Строка проверки: галочка, крестик или вопрос, если ответа ещё нет. */
export function Check({
	ok,
	label,
	loading,
	neutral,
	title,
}: {
	ok: boolean | null
	label: string
	loading?: boolean
	/** Не ошибка, а просто «нет»: не красим в красный. */
	neutral?: boolean
	title?: string
}) {
	if (loading) {
		return (
			<span className="pub-check">
				<span className="spinner" /> {label}
			</span>
		)
	}

	const mark = ok === null ? "?" : ok ? "✓" : "✕"
	const tone =
		ok === null ? "" : ok ? " pub-check--ok" : neutral ? "" : " pub-check--no"

	return (
		<span className={"pub-check" + tone} title={title}>
			<b>{mark}</b> {label}
		</span>
	)
}
